import { FiAlertTriangle } from 'react-icons/fi';
import SelectTextField from '../../../molecules/SelectTextField';
import TextEdit from '../../../molecules/TextEdit';
import DateField from '../../../molecules/DateField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import { CourtDecisionType, type SelectOption } from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';
import useLegalCaseCourtDecisionForm, { residualErrors } from '../../../../hooks/useLegalCaseCourtDecisionForm';
import type { AddCourtDecisionValue } from '../../../../hooks/useLegalCaseCourtDecisions';

interface LegalCaseCourtDecisionFormProps {
    typeOptions: SelectOption[];
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: AddCourtDecisionValue) => Promise<boolean>;
    onCancel: () => void;
}

// Só JSX + bindings: estado e regras do formulário vivem em useLegalCaseCourtDecisionForm.
const LegalCaseCourtDecisionForm = ({
    typeOptions,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: LegalCaseCourtDecisionFormProps) => {
    const form = useLegalCaseCourtDecisionForm({ onSubmit });

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <SelectTextField
                    label="Tipo da decisão"
                    items={typeOptions}
                    value={form.decisionType}
                    onChange={(e) => form.setDecisionType(e.target.value as CourtDecisionType)}
                    errorMessage={getErrorMessage(errorFields, 'decisionType')}
                />
                <DateField
                    label="Data da decisão"
                    value={form.decisionDate}
                    onChange={form.setDecisionDate}
                    errorMessage={getErrorMessage(errorFields, 'decisionDate')}
                />
            </div>

            <TextEdit
                label="Teor (opcional)"
                value={form.summary}
                onChange={(e) => form.setSummary(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'summary')}
            />

            {/* Erros de validação (422) sem campo próprio na tela — não deixa sumir. */}
            {residualErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={form.submit}
                    disabled={isSaving || !form.canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Registrar decisão'}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseCourtDecisionForm;
